import {
  ColumnTable,
  Icon,
  OutlinedSelect,
  OutlinedSelectOptionType,
} from '@octano/global-ui';
import { Col } from 'reactstrap';
import { formatDate } from '../../../../../../utils/dates';
import {
  respondentTypes,
  Survey,
  SurveyContext,
  surveyContextTexts,
  SurveyStatus,
  surveyStatusTexts,
} from '../../../types/Survey';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../../../locales/translations';

interface useColumnActions {
  onClickCopy: (row: any) => void;
  onClickEdit: (row: any) => void;
  onChangeStatus: (row: Survey, newStatus: SurveyStatus) => void;
  statusOptions: OutlinedSelectOptionType[];
}

export default function useColumns(
  props: useColumnActions,
): ColumnTable<Survey>[] {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_SURVEY);
  const { onClickCopy, onClickEdit, onChangeStatus, statusOptions } = props;

  return [
    {
      columnName: 'title',
      headerText: t('surveyList.headers.title'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
    },
    {
      columnName: 'context',
      headerText: t('surveyList.headers.context'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options) =>
        surveyContextTexts[options.row.context as SurveyContext] ||
        options.row.context,
    },
    {
      columnName: 'period',
      headerText: t('surveyList.headers.period'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options) => options.row.surveyPeriod[0].period.name,
    },
    {
      columnName: 'startDate',
      headerText: t('surveyList.headers.startDate'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options) => formatDate(options.row?.startDate, 'DD/MM/YYYY'),
    },
    {
      columnName: 'endDate',
      headerText: t('surveyList.headers.endDate'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options) => formatDate(options.row?.endDate, 'DD/MM/YYYY'),
    },
    {
      columnName: 'respondents',
      headerText: t('surveyList.headers.respondents'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: (options) =>
        respondentTypes[options.row.context as SurveyContext] ||
        options.row.context,
    },
    {
      columnName: 'status',
      headerText: t('surveyList.headers.status'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        const current = statusOptions.find(
          (opt) => `${opt.value}` === `${row.status}`,
        );
        return (
          <div style={{ width: '150px', margin: '0 auto' }}>
            {row.status === SurveyStatus.Finished ? (
              <Col className="text-center">
                <Icon name="success" color="success" />
                <span style={{ marginLeft: '10px' }}>
                  {surveyStatusTexts[row.status]}
                </span>
              </Col>
            ) : (
              <OutlinedSelect
                name={`status-${row.id}`}
                options={statusOptions}
                value={current}
                isClearable={false}
                isSearchable={false}
                onChange={(opt) => {
                  if (opt) onChangeStatus(row, opt.value);
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      columnName: 'actions',
      headerText: t('surveyList.headers.actions'),
      tdClassName: 'text-center',
      thClassName: 'text-center',
      cellFormat: ({ row }) => {
        const isFinished = row.status === SurveyStatus.Finished;
        return (
          <div className="d-flex justify-content-end">
            <button className="btn" onClick={() => onClickCopy(row)}>
              <Icon color="primary" name="duplicate" />
            </button>

            <button className="btn" onClick={() => onClickEdit(row)}>
              {isFinished ? (
                <Icon name="eye" color={'primary'} />
              ) : (
                <Icon name="edit" color={'primary'} />
              )}
            </button>
          </div>
        );
      },
    },
  ];
}
